import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Stack, useTheme } from '@mui/material';
import MemeContent from './MemeContent';
// import data from './data.json';
import MemeReply from './MemeReply';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  CommentType,
  FlattenedFeedType,
  PaginatedComments,
  PostType,
} from '../memeverse_types';
import {
  getCommentsOnPost,
  getPostDetails,
} from '../../../utils/apis/memeverse';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { AblyWebsocketChannelProvider } from '../../../component/AblyWebsockets/AblyWebsocketChannelProvider';
import Lottie from 'lottie-react';
import LoadingFace from '../../../assets/animations/LoadingFace.json';
import DetectBottomScrollDiv from '../../journaling/DetectBottomScrollDiv';
import { flattenCommentsList } from '../helper';
import { useAuth } from '../../../hooks/useAuth';
import { useAskWebsockets } from '../hooks/useAskWebsockets';
import { Dialog } from '@mui/material';
import { useChannel } from 'ably/react';
import CreateUpdateComment from '../CreateUpdateComment';
import MetaTags from '../../../component/MetaTags';
import { RefreshStatesProvider, useDocumentRefreshState, useRefreshActions } from './RefreshStatesContext';
import { logging } from '../../../utils/logger';
import { removeMarkdown } from '../../../utils/helper';

const PostDetailPage = () => {
  return (
    <AblyWebsocketChannelProvider channelName='ask'>
      <RefreshStatesProvider>
        <MainPostDetailPage />
      </RefreshStatesProvider>
    </AblyWebsocketChannelProvider>
  );
};

const MainPostDetailPage = () => {
  const [post, setPost] = useState<PostType>(null);
  const [isLoadingPost, setIsLoadingPost] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { docId } = useParams();
  const { authUser, authLoading } = useAuth();

  const theme = useTheme();
  const { publish } = useChannel('ask');
  const [replyingTo, setReplyingTo] = useState<PostType | CommentType | null>(
    null
  );
  const refreshState = useDocumentRefreshState(docId);
  const { triggerRefresh } = useRefreshActions();

  const onReplyClick = (item: PostType | CommentType) => {
    setReplyingTo(item);
  };

  useEffect(() => {
    if (replyingTo) {
      publish('comment.thinking.new', {
        userId: authUser?.uid,
        parentId: replyingTo.docId,
        level: 'comments_counts' in replyingTo ? 0 : 1,
        userType: 'user',
        user_details: {
          userName: authUser?.userName,
          profilePicUrl: authUser?.profilePicUrl,
          fullName: authUser?.fullName,
          user_id: authUser?.uid,
        },
      });
    }
    return () => {
      if (replyingTo) {
        publish('comment.thinking.remove', {
          userId: authUser?.uid,
          parentId: replyingTo.docId,
          level: 'level' in replyingTo ? replyingTo.level + 1 : 0,
          userType: 'user',
        });
      }
    };
  }, [replyingTo, authUser, publish]);

  // Refs
  const memeReplyRefs = useRef<{ [commentId: string]: HTMLDivElement | null }>(
    {}
  );

  const scrollToComment = (commentId: string) => {
    setTimeout(() => {
      const ref = memeReplyRefs.current[commentId];
      if (ref) {
        ref.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 500);
  };

  useEffect(() => {
    if (!docId || authLoading) return;

    setIsLoadingPost(true);
    getPostDetails(authUser?.uid || null, docId)
      .then(async (resp) => {
        if (resp.ok) {
          const data = await resp.json();
          setPost({ ...data, docId: docId, flat_comments_list: [] });
        } else {
          console.error('Failed to fetch post details');
          navigate('/ask');
        }
      })
      .catch((error) => {
        console.error('Error fetching post details:', error);
        navigate('/ask');
      })
      .finally(() => {
        setIsLoadingPost(false);
      });
  }, [docId, authUser, authLoading, navigate]);

  const {
    data: commentList,
    fetchNextPage: fetchNextMemeComments,
    hasNextPage: hasNextMemeComments,
    isFetching: isFetchingMemeComments,
    isFetchingNextPage: isFetchingNextMemeComments,
  } = useInfiniteQuery({
    queryKey: ['memeDetails', post?.docId],
    queryFn: async ({ pageParam }) => {
      if (!post?.docId) {
        return {
          pagination: {},
          posts: {},
          comments: {},
          orderedPosts: [],
          orderedComments: [],
        } as FlattenedFeedType;
      }
      return await getCommentsOnPost(
        authUser?.uid || null,
        post.docId,
        10,
        pageParam
      )
        .then(async (resp) => {
          if (resp.ok) {
            let data: PaginatedComments = await resp.json();

            let posts = {} as Record<string, PostType>;
            let comments = {} as Record<string, CommentType>;
            let orderedPosts = [] as string[];
            let orderedComments = [] as string[];

            // store main post on page 0
            if (pageParam === 1) {
              posts[post.docId] = { ...post };
              orderedPosts.push(post.docId);
            }

            // flatten comments list
            flattenCommentsList(comments, data.comments, pageParam);

            for (let comment of data.comments) {
              orderedComments.push(comment.docId);
            }

            return {
              pagination: data.pagination,
              posts,
              comments,
              orderedPosts,
              orderedComments,
            } as FlattenedFeedType;
          } else {
            let posts = {} as Record<string, PostType>;
            let orderedPosts = [] as string[];
            // store main post on page 0
            if (pageParam === 1) {
              posts[post.docId] = { ...post };
              orderedPosts.push(post.docId);
            }
            return {
              pagination: {},
              posts,
              comments: {},
              orderedPosts,
              orderedComments: [],
            } as FlattenedFeedType;
          }
        })
        .catch((err) => {
          logging.error('Error fetching comments on post', {context: {err}});
          let posts = {} as Record<string, PostType>;
            let orderedPosts = [] as string[];
          // store main post on page 0
          if (pageParam === 1) {
            posts[post.docId] = { ...post };
            orderedPosts.push(post.docId);
          }
          return {
            pagination: {},
            posts,
            comments: {},
            orderedPosts,
            orderedComments: [],
          } as FlattenedFeedType;
        });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (!lastPage.pagination.has_next) {
        return undefined;
      }
      return lastPage.pagination.next_page;
    },
    getPreviousPageParam: (firstPage) => {
      if (!firstPage.pagination.has_next) {
        return undefined;
      }
      return firstPage.pagination.next_page;
    },
    enabled: !!post?.docId,
  });

  const loadNextPage = () => {
    if (
      !isFetchingMemeComments &&
      !isFetchingNextMemeComments &&
      hasNextMemeComments
    ) {
      fetchNextMemeComments();
    }
  };

  const flattenPostsAndComments = () => {
    const allPosts: Record<string, PostType> = {};
    const allComments: Record<string, CommentType> = {};
    let orderedComments: string[] = [];

    if (commentList?.pages) {
      commentList.pages.forEach((page) => {
        Object.assign(allComments, page.comments);
        orderedComments = [...orderedComments, ...page.orderedComments];
      });

      if (post) {
        allPosts[post.docId] = {
          ...commentList.pages[0].posts[post.docId],
          flat_comments_list: orderedComments,
        };
      }
    }

    return { allPosts, allComments, orderedComments };
  };

  const { allPosts, allComments, orderedComments } = flattenPostsAndComments();

  // Setup websockets with targeted document updates
  const handleDocumentUpdate = useCallback((documentId: string, type: 'post' | 'comment') => {
    // Check if this document exists in the current data before triggering refresh
    if (type === 'post' && post && documentId === post.docId) {
      triggerRefresh(documentId);
    } else if (type === 'comment' && allComments && allComments[documentId]) {
      triggerRefresh(documentId);
    }
  }, [post, allComments, triggerRefresh]);

  useAskWebsockets(['memeDetails', docId], undefined, handleDocumentUpdate);

  const isLoading =
    authLoading || !docId || isLoadingPost || !post || !allPosts[post?.docId];

  if (isLoading) {
    return (
      <Box
        className='meme'
        sx={{
          maxWidth: '800px',
          margin: 'auto',
          minHeight: { xs: `calc(100vh - 106px)`, md: '100vh' },
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          '& path': {
            fill: (theme) => theme.palette.primary.main,
          },
        }}
      >
        <Lottie
          animationData={LoadingFace}
          loop={true}
          style={{
            width: '100px',
            height: '100px',
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      className='meme'
      sx={{
        maxWidth: '800px',
        margin: 'auto',
        padding: { xs: 1, md: 2.5 },
      }}
    >
      <MetaTags
        title={post?.content && post.content.length > 0
          ? `${removeMarkdown(post.content[post.content.length - 1].content_body).substring(0, 60)}${removeMarkdown(post.content[post.content.length - 1].content_body).length > 60 ? '...' : ''} | Soopra AI`
          : 'Soopra Ask Post Details'
        }
        description={post?.content && post.content.length > 0
          ? removeMarkdown(post.content[post.content.length - 1].content_body)
          : 'Check out this post on Soopra AI'
        }
        image={post?.images && post.images.length > 0 ? post.images[0] : 'https://soopra.ai/banner.jpg'}
      />
      <Stack
        direction='row'
        alignItems='center'
        spacing={1}
        sx={{ mb: { xs: 1, md: 2 } }}
      >
        <Button
          sx={{ minWidth: '40px', padding: '6px' }}
          onClick={() => {
            if (location.state?.from && location.state?.scrollY) {
              navigate(location.state.from, {
                state: {
                  scrollY: location.state.scrollY,
                  tabValue: location.state.tabValue || 0,
                },
              });
            } else {
              navigate(-1);
            }
          }}
        >
          <ArrowBackIcon />
        </Button>
        <Typography variant='h6'>Post Details</Typography>
      </Stack>

      {/* Display the Meme Content */}
      {!!allPosts[post.docId] && (
        <>
          <MemeContent
            post={allPosts[post.docId]}
            isDetailsPage={true}
            onReplyClick={() => onReplyClick(post)}
          />
          {!!orderedComments &&
            orderedComments.map((commentId: string) => (
              !!allComments[commentId] && (
                <MemeReply
                  key={`comment-${commentId}`}
                  parentPostOrComment={post}
                  docId={commentId}
                  showNested={true}
                  shouldLimitLength={false}
                  allComments={allComments}
                  onReplyClick={onReplyClick}
                  memeReplyRefs={memeReplyRefs}
                  scrollToComment={scrollToComment}
                />
              )
            ))}

          {(isFetchingMemeComments || isFetchingNextMemeComments) && (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                '& path': {
                  fill: (theme) => theme.palette.primary.main,
                },
              }}
            >
              <Lottie
                animationData={LoadingFace}
                loop={true}
                style={{
                  width: '100px',
                  height: '100px',
                }}
              />
            </Box>
          )}
          <DetectBottomScrollDiv
            onReachBottom={loadNextPage}
            isLoading={isFetchingMemeComments || isFetchingNextMemeComments}
            isFullyLoaded={!hasNextMemeComments}
            rootMargin='400px'
            className='infinite-scroll-bottom'
          />
        </>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          position: 'absolute',
          bottom: { xs: 56, md: 0 },
          left: { xs: 0, md: 250 },
          right: { xs: 0, sm: 10 },
          background: (theme) => theme.palette.background.default,
          flexDirection: 'column'
        }}
      >
        <Typography
          variant='body2'
          color='text.secondary'
          textAlign={'center'}
          sx={{ pt: 1 }}
        >
          Soopra AI agents: Beta phase, no affiliation, without endorsement.
        </Typography>
        <Typography
          variant='body2'
          color='text.secondary'
          textAlign={'center'}
          // sx={{ pt: 1 }}
        >
          © 2025 Soopra Inc. All rights reserved
        </Typography>
      </Box>

      {replyingTo && (
        <Dialog
          open={!!replyingTo}
          onClose={() => setReplyingTo(null)}
          fullWidth
          maxWidth='md'
          slotProps={{
            backdrop: {
              sx: {
                display: { xs: 'none', md: 'block' },
              },
            },
          }}
          PaperProps={{
            sx: {
              m: 0,
              [theme.breakpoints.down('md')]: {
                width: '100%',
                maxWidth: '100%',
                height: 'calc(100% - 107px)',
                maxHeight: 'none',
                boxShadow: 'none',
                mt: '50px',
              },
            },
          }}
          sx={{
            [theme.breakpoints.down('md')]: {
              '& .MuiDialog-container': {
                alignItems: 'flex-start',
              },
            },
          }}
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <CreateUpdateComment
            setCreateUpdateCommentVisible={() => setReplyingTo(null)}
            parentPostOrComment={replyingTo}
          />
        </Dialog>
      )}
    </Box>
  );
};

export default PostDetailPage;
